import { CustomAICharacter } from '../types/CustomAICharacter';
import { MONSTER_DATABASE } from '../constants/monsters';
import { migrateAIConfig, withVersion } from '../ai/aiConfigVersion';

export const STORAGE_KEY = 'monster-buttle-custom-ai';
export const MAX_CUSTOM_AI_CHARACTERS = 4;

const VALID_MONSTER_IDS = new Set(MONSTER_DATABASE.map((m) => m.id));

/**
 * カスタムAIキャラクターデータのバリデーション
 */
export function isValidCustomAICharacter(data: unknown): data is CustomAICharacter {
  if (!data || typeof data !== 'object') return false;
  const d = data as Record<string, unknown>;
  return (
    typeof d.id === 'string' &&
    typeof d.name === 'string' &&
    typeof d.monsterId === 'string' &&
    VALID_MONSTER_IDS.has(d.monsterId) &&
    d.aiConfig !== null &&
    typeof d.aiConfig === 'object' &&
    typeof d.createdAt === 'number' &&
    typeof d.updatedAt === 'number'
  );
}

/**
 * 全カスタムAIキャラクターをLocalStorageから読み込む
 */
export function loadCustomAICharacters(): CustomAICharacter[] {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return [];
    const parsed = JSON.parse(stored);
    if (!Array.isArray(parsed)) return [];
    return parsed
      .filter(isValidCustomAICharacter)
      .map((c) => ({ ...c, aiConfig: migrateAIConfig(c.aiConfig) }));
  } catch {
    return [];
  }
}

/**
 * カスタムAIキャラクターを保存する
 * @throws 最大保存数を超える新規追加時
 */
export function saveCustomAICharacter(char: CustomAICharacter): void {
  const existing = loadCustomAICharacters();
  const existingIndex = existing.findIndex((c) => c.id === char.id);

  // 保存時は AIConfig に __version を付与する（マイグレーション用）
  const charWithVersion: CustomAICharacter = {
    ...char,
    aiConfig: withVersion(char.aiConfig),
  };

  if (existingIndex >= 0) {
    existing[existingIndex] = charWithVersion;
  } else {
    if (existing.length >= MAX_CUSTOM_AI_CHARACTERS) {
      throw new Error(`最大${MAX_CUSTOM_AI_CHARACTERS}体まで保存できます`);
    }
    existing.push(charWithVersion);
  }

  localStorage.setItem(STORAGE_KEY, JSON.stringify(existing));
}

/**
 * 指定IDのカスタムAIキャラクターを削除する
 */
export function deleteCustomAICharacter(id: string): void {
  const existing = loadCustomAICharacters();
  const filtered = existing.filter((c) => c.id !== id);
  localStorage.setItem(STORAGE_KEY, JSON.stringify(filtered));
}

/**
 * 保存済みカスタムAIキャラクター数を返す
 */
export function getCustomAICharacterCount(): number {
  return loadCustomAICharacters().length;
}

/**
 * 指定IDのカスタムAIキャラクターを取得する
 */
export function getCustomAICharacterById(id: string): CustomAICharacter | undefined {
  return loadCustomAICharacters().find((c) => c.id === id);
}
